import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useIsMobile } from '../hooks/use-is-mobile'
import { MultiCombo } from './multi-combo'
import { useScrollRoom } from './use-scroll-room'
import { activeCount } from './filters-model'
import { TimeRangeFilter } from '../date/time-range-filter'
import { actorChip, auditErrorText, categoryLabel, countryName } from './entry-format'
import './audit.scss'
import { formatCount } from '../sso-workspace/format'
import { ErrorReference } from '../error/error-reference'
import { SEARCH_FIELD_PROPS } from '../component/search-field-props'

// The audit filters panel (spec §5; mockup "Audit log"): category, who,
// record type, when, app, network and location, and the two toggles.
// Stateless apart from the IP box: the page owns `filters` (in its URL) and
// gets every change through onChange(next).
//
// facets: GET /facets ({ categories, actors, recordTypes, apps, countries,
// truncated }); null while loading or after a failed read (error), when the
// lists have no counts. groups: the organization's groups [{ id, name }] for
// Who. show: the sections to render (default all); History hides record type
// and location. extra: the page's own controls (the super admin's
// organization picker). allTime offers "Any time" (History only).
//
// On a phone (useIsMobile) the panel sits behind "Filters (n)"; up to 860 px
// the stylesheet does the same. Beside the results (the Audit log's two
// columns, audit-log.scss) it sticks while they scroll, and its card scrolls
// by itself when it is taller than the room it sticks in (useScrollRoom).
const ALL = ['category', 'who', 'type', 'when', 'app', 'network', 'toggles']

const automationLabel = key => key.slice(5).replace(/^(job|integration):/, '')

const whoGroups = (facets, groups) => {
  const actors = (facets && facets.actors) || []
  const people = []
  const autos = []
  const others = []
  actors.forEach(f => {
    const key = f.key || ''
    const chip = actorChip(f)
    if (!chip.clickable) others.push({ value: key || 'none', label: chip.text, count: chip.count, disabled: true })
    else if (key.startsWith('auto:')) autos.push({ value: key, label: automationLabel(key), count: f.count })
    else people.push({ value: key, label: chip.text, sub: chip.note || undefined, count: f.count })
  })
  return [
    { label: 'Groups', options: (groups || []).map(g => ({ value: `group:${g.id}`, label: g.name })) },
    { label: 'People', options: people },
    { label: 'Automations', options: autos },
    { label: 'Other', options: others }
  ].filter(g => g.options.length)
}

// A facet list as one MultiCombo group, keeping picked values the facets
// no longer list (so they can still be seen and cleared).
const facetOptions = (list, label) => (list || []).map(f => ({ value: f.key, label: label(f), count: f.count }))

const Section = ({ title, children }) =>
  <section className="au-facet">
    <h3 className="au-facet-h">{title}</h3>
    {children}
  </section>

// sessionLabel: the session filter in words ("This session", session-label.js),
// never its id.
export const AuditFilters = ({ filters: f, onChange, facets, groups, show, extra, error, allTime = false, sessionLabel }) => {
  const mobile = useIsMobile()
  const column = useRef(null)
  useScrollRoom(column)
  const [open, setOpen] = useState(false)
  const [ip, setIP] = useState(f.ip)
  const on = name => (show ? show.has(name) : ALL.includes(name))

  useEffect(() => setIP(f.ip), [f.ip])

  const set = patch => onChange({ ...f, ...patch })
  const toggleIn = (key, v) => set({ [key]: f[key].includes(v) ? f[key].filter(x => x !== v) : [...f[key], v] })
  const commitIP = () => {
    const v = ip.trim()
    if (v !== f.ip) set({ ip: v })
  }

  const who = useMemo(() => whoGroups(facets, groups), [facets, groups])
  const whoValue = [...f.groups.map(g => `group:${g}`), ...f.actors]
  const onWho = v => set({ groups: v.filter(x => x.startsWith('group:')).map(x => x.slice(6)), actors: v.filter(x => !x.startsWith('group:')) })

  const categories = useMemo(() => {
    const listed = ((facets && facets.categories) || []).map(c => ({ key: c.key, label: c.label || categoryLabel(c.key), count: c.count }))
    const extraPicked = f.categories.filter(k => !listed.some(c => c.key === k)).map(k => ({ key: k, label: categoryLabel(k) }))
    return [...listed, ...extraPicked]
  }, [facets, f.categories])

  const n = activeCount(f)
  const errorText = error ? auditErrorText(error, "Counts couldn't be loaded.") : null
  const applied = [
    ...f.actions.map(a => ({ key: `action:${a}`, what: 'Action', value: a, code: true, remove: () => set({ actions: f.actions.filter(x => x !== a) }) })),
    ...f.topics.map(t => ({ key: `topic:${t}`, what: 'Topic', value: t, remove: () => set({ topics: f.topics.filter(x => x !== t) }) })),
    ...(f.request ? [{ key: 'request', what: 'Request', value: f.request, code: true, remove: () => set({ request: '' }) }] : []),
    ...(f.session ? [{ key: 'session', value: sessionLabel || 'One session', removeLabel: 'Remove the session filter', remove: () => set({ session: '' }) }] : [])
  ]

  const button = (
    <button type="button" className={`btn sw-btn btn-sm au-filters-btn${mobile ? '' : ' au-only-narrow'}`} aria-expanded={open} aria-controls="au-facets" onClick={() => setOpen(o => !o)}>
      {n ? `Filters (${n})` : 'Filters'}
    </button>
  )

  if (mobile && !open) return <div className="au-filters" ref={column}>{button}</div>

  return (
    <div className="au-filters" ref={column}>
      {button}
      <aside id="au-facets" className={`au-facets${open ? ' au-open' : ''}`} aria-label="Filters">
        {errorText && <p className="text-danger small m-0" role="alert">{errorText}</p>}
        {errorText && <ErrorReference error={error} />}
        {facets && facets.truncated && <p className="sw-hint small m-0">Counts cover only the newest entries.</p>}

        {applied.length > 0 &&
          <div role="group" aria-label="Also filtered by" className="au-applied">
            {applied.map(a =>
              <span className="au-tag" key={a.key}>
                {a.what && <><span className="au-tag-what">{a.what}</span>{' '}</>}{a.code ? <code>{a.value}</code> : a.value}
                <button type="button" aria-label={a.removeLabel || `Remove ${a.what.toLowerCase()} ${a.value}`} onClick={a.remove}>×</button>
              </span>)}
          </div>}

        {on('category') && categories.length > 0 &&
          <Section title="Category">
            {categories.map(c =>
              <label className="au-check" key={c.key}>
                <input type="checkbox" checked={f.categories.includes(c.key)} onChange={() => toggleIn('categories', c.key)} />
                <span>{c.label}</span>
                {c.count !== undefined && <span className="au-n">{formatCount(c.count)}</span>}
              </label>)}
          </Section>}

        {on('who') &&
          <Section title="Who">
            <p className="sw-hint small au-facet-note">People, groups or automations. Choose several.</p>
            <MultiCombo id="au-who" label="Who" groups={who} value={whoValue} onChange={onWho} placeholder="Search people and groups" />
          </Section>}

        {on('type') &&
          <Section title="Record type">
            <MultiCombo id="au-type" label="Record type" groups={[{ label: 'Record types', options: facetOptions(facets && facets.recordTypes, x => x.label || x.key) }]} value={f.recordTypes} onChange={v => set({ recordTypes: v })} placeholder="Search record types" />
          </Section>}

        {on('when') &&
          <Section title="When">
            <TimeRangeFilter value={f} onChange={onChange} allTime={allTime} capped />
          </Section>}

        {on('app') &&
          <Section title="App or device">
            <MultiCombo id="au-app" label="App or device" groups={[{ label: 'Apps and devices', options: facetOptions(facets && facets.apps, x => x.label || x.key) }]} value={f.apps} onChange={v => set({ apps: v })} placeholder="Search apps" />
          </Section>}

        {on('network') &&
          <Section title="Network and location">
            <input
              {...SEARCH_FIELD_PROPS}
              name="audit-ip-search"
              className="form-control form-control-sm au-ip"
              placeholder="IP or range, e.g. 203.0.113.0/24"
              aria-label="IP address or range"
              value={ip}
              onChange={e => setIP(e.target.value)}
              onBlur={commitIP}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  commitIP()
                }
              }}
            />
            <MultiCombo id="au-geo" label="Location" groups={[{ label: 'Countries', options: facetOptions(facets && facets.countries, x => countryName(x.key)) }]} value={f.countries} onChange={v => set({ countries: v })} placeholder="Search countries" />
            <p className="sw-hint small au-facet-note">Location comes from the IP address and is looked up when you read the entry.</p>
          </Section>}

        {on('toggles') &&
          <section className="au-facet au-toggles">
            <label className="au-check"><input type="checkbox" checked={f.includeViews} onChange={e => set({ includeViews: e.target.checked })} />Include views</label>
            <label className="au-check"><input type="checkbox" checked={f.includeAutomatic} onChange={e => set({ includeAutomatic: e.target.checked })} />Include automatic changes</label>
          </section>}

        {extra}
      </aside>
    </div>
  )
}
